
import Form from '@/ui-kit/components/form';
import FormField from '@/ui-kit/components/form/form-field';
import {
  StyleColorSchemeType,
  StyleFontWeightType,
  StylePositionType,
  StyleSizeType,
} from '@/ui-kit/styles/enums';
import { LayoutItemColumn, LayoutItemProperty } from '@/ui-kit/components/layout-grid/item/enums';
import { ControlEventType, ControlType } from '@/ui-kit/components/form/controls/enums';
import { LayoutContainerProperty } from '@/ui-kit/components/layout-grid/container/enums';
import Typography from '@/ui-kit/components/typography';
import { TypographyProperty, TypographyType } from '@/ui-kit/components/typography/enums';
import Gutter from '@/ui-kit/components/gutter';
import { GutterProperty } from '@/ui-kit/components/gutter/enums';
import { FormProperty } from '@/components/features/sold-data/shared/components/enums';
import { FormEventType, FormFieldProperty } from '@/ui-kit/components/form/enums';
import BaseForm from '@/components/features/sold-data/shared/components/mixins/BaseForm';
import FormProps from '@/components/features/sold-data/shared/components/mixins/FormProps';
import {
  MARKET_DATA_FILTER_FORM_DEFAULT_VALUE,
  MarketDataFilterFormControlName,
} from '@/components/features/market-data/components/filters/MarketDataFiltersFormControls';
import { ButtonsProperty } from '@/components/features/sold-data/shared/components/forms/buttons/enums';
import { ButtonFillType } from '@/ui-kit/components/button/enums';
import { BaseFilterProperty } from '@/components/features/sold-data/shared/components/base-filter/enums';
import { MarketDataEvent } from '@/constants/AnalyticEvents';
import TrackEvent from '@/components/features/sold-data/shared/mixins/TrackEvent';
import FormButtons from '@/components/features/sold-data/shared/components/forms/buttons';
import LayoutGridContainer from '@/ui-kit/components/layout-grid/container';
import LayoutGridItem from '@/ui-kit/components/layout-grid/item';
import { SelectProperty } from '@/ui-kit/components/form/controls/components/select/enums';
import { InputFillType, InputSlotType } from '@/ui-kit/components/form/controls/shared/input/enums';
import { PROVINCE_OPTIONS } from '@/components/features/sold-data/shared/components/forms/location/constants';
import {
  AutocompleteFilterStrategy,
  AutocompleteFilterType,
  AutocompleteProperty,
} from '@/ui-kit/components/form/controls/components/autocomplete/enums';
import AddressDictionaries from '@/components/shared/forms/address/mixins/AddressDictionaries';
import { DatePeriodMode } from '@/components/features/sold-data/shared/components/forms/date-period/enums';
import {
  DATE_PERIOD_OPTIONS_BY_MODE,
} from '@/components/features/sold-data/shared/components/forms/date-period/constants';
import { MinMaxSelectId } from '@/ui-kit/components/min-max-select/enums';
import MinMaxSelect from '@/ui-kit/components/min-max-select';
import {
  DEFAULT_MULTI_BATHS_OPTIONS,
  DEFAULT_MULTI_BEDS_OPTIONS,
  PRICE_RANGE_OPTIONS,
  YEAR_BUILT_OPTIONS,
} from '@/components/filters/constants/options';
import OptionsHelper from '@/utils/OptionsHelper';
import { SEARCH_BUILDING_CLASSES } from '@/components/features/search/shared/constants/BuildingClasses';
import IconHintTooltip from '@/components/shared/tooltips/icon-hint-tooltip';
import { IconHintTooltipProperty } from '@/components/shared/tooltips/icon-hint-tooltip/enum';
import { TooltipMessage } from '@/ui-kit/components/tooltip/enum';
import Auth from '@/mixins/auth';

export default {
  name: 'MarketDataFilters',
  components: {
    Form,
    FormField,
    FormButtons,
    LayoutGridContainer,
    LayoutGridItem,
    Typography,
    Gutter,
    MinMaxSelect,
    IconHintTooltip,
  },
  mixins: [BaseForm, FormProps, TrackEvent, AddressDictionaries, Auth],
  data() {
    return {
      StyleSizeType,
      StyleColorSchemeType,
      FormFieldProperty,
      ControlType,
      ControlEventType,
      MarketDataFilterFormControlName,
      FormProperty,
      FormEventType,
      ButtonsProperty,
      BaseFilterProperty,
      LayoutItemProperty,
      LayoutItemColumn,
      LayoutContainerProperty,
      TypographyProperty,
      TypographyType,
      StyleFontWeightType,
      GutterProperty,
      StylePositionType,
      ButtonFillType,
      InputSlotType,
      IconHintTooltipProperty,
      TooltipMessage,
      YEAR_BUILT_OPTIONS,
      PRICE_RANGE_OPTIONS,
      submitTrackEvent: MarketDataEvent.ApplyFiltersClick,
      trackEventByControlName: {
        [MarketDataFilterFormControlName.TransactionDate]: MarketDataEvent.DateRangeSelected,
        [MarketDataFilterFormControlName.Province]: MarketDataEvent.ProvinceSelected,
        [MarketDataFilterFormControlName.City]: MarketDataEvent.CitySelected,
        [MarketDataFilterFormControlName.Community]: {
          select: MarketDataEvent.CommunitySelected,
          unselect: MarketDataEvent.CommunityUnselected,
        },
        [MarketDataFilterFormControlName.Community]: {
          select: MarketDataEvent.CommunitySelected,
          unselect: MarketDataEvent.CommunityUnselected,
        },
        [MarketDataFilterFormControlName.BuildingClass]: {
          select: MarketDataEvent.PropertyTypeSelected,
          unselect: MarketDataEvent.PropertyTypeUnselected,
        },
        [MarketDataFilterFormControlName.Beds]: {
          select: MarketDataEvent.BedsSelected,
          unselect: MarketDataEvent.BedsUnselected,
        },
        [MarketDataFilterFormControlName.Baths]: {
          select: MarketDataEvent.BathsSelected,
          unselect: MarketDataEvent.BathsUnselected,
        },
        [MarketDataFilterFormControlName.MinPrice]: MarketDataEvent.MinPriceSelected,
        [MarketDataFilterFormControlName.MaxPrice]: MarketDataEvent.MaxPriceSelected,
        [MarketDataFilterFormControlName.MinYearBuilt]: MarketDataEvent.YearBuiltSelected,
        [MarketDataFilterFormControlName.MaxYearBuilt]: MarketDataEvent.YearBuiltSelected,
      },
    };
  },
  computed: {
    user() {
      return this.$store.state.user.userData;
    },

    province() {
      return this.provinceControl?.value;
    },
    provinceControl() {
      return this.getControl(MarketDataFilterFormControlName.Province);
    },
    provinceInputProps() {
      return {
        [SelectProperty.Name]: MarketDataFilterFormControlName.Province,
        [SelectProperty.FillType]: InputFillType.Underlined,
        [SelectProperty.Label]: 'Province',
        [SelectProperty.Options]: PROVINCE_OPTIONS,
      };
    },

    city() {
      return this.cityControl?.value;
    },
    cityControl() {
      return this.getControl(MarketDataFilterFormControlName.City);
    },
    cityInputProps() {
      return {
        [AutocompleteProperty.Name]: MarketDataFilterFormControlName.City,
        [AutocompleteProperty.FillType]: InputFillType.Underlined,
        [AutocompleteProperty.FilterStrategy]: AutocompleteFilterStrategy.Sync,
        [AutocompleteProperty.Options]: this.citiesOptions,
        [AutocompleteProperty.Label]: 'City',
        [AutocompleteProperty.IsLoading]: this.areAddressDictionariesLoading,
        [AutocompleteProperty.Disabled]: !this.province,
        [AutocompleteProperty.FilterType]: AutocompleteFilterType.StartsWith,
      };
    },

    transactionDate() {
      return this.transactionDateControl?.value;
    },
    transactionDateControl() {
      return this.getControl(MarketDataFilterFormControlName.TransactionDate);
    },
    transactionDateInputProps() {
      return {
        [SelectProperty.FillType]: InputFillType.Underlined,
        [SelectProperty.Label]: 'Date Range',
        [SelectProperty.Options]: DATE_PERIOD_OPTIONS_BY_MODE[DatePeriodMode.Shortest],
      };
    },

    communityControl() {
      return this.getControl(MarketDataFilterFormControlName.Community);
    },
    communityInputProps() {
      return {
        [AutocompleteProperty.Name]: MarketDataFilterFormControlName.Community,
        [AutocompleteProperty.FillType]: InputFillType.Underlined,
        [AutocompleteProperty.FilterStrategy]: AutocompleteFilterStrategy.Sync,
        [AutocompleteProperty.Options]: this.communitiesOptions,
        [AutocompleteProperty.Label]: 'Community',
        [AutocompleteProperty.IsLoading]: this.areAddressDictionariesLoading,
        [AutocompleteProperty.Disabled]: !this.city,
        [AutocompleteProperty.FilterType]: AutocompleteFilterType.StartsWith,
        [SelectProperty.Multiple]: true,
      };
    },

    buildingClassInputProps() {
      return {
        [AutocompleteProperty.Name]: MarketDataFilterFormControlName.BuildingClass,
        [AutocompleteProperty.Options]: OptionsHelper.mapValuesIntoOptions(
          Object.values(SEARCH_BUILDING_CLASSES),
        ),
        [AutocompleteProperty.Label]: 'Property Types',
        [SelectProperty.Multiple]: true,
        [AutocompleteProperty.FillType]: InputFillType.Underlined,
        [AutocompleteProperty.FilterStrategy]: AutocompleteFilterStrategy.Sync,
        [AutocompleteProperty.FilterType]: AutocompleteFilterType.StartsWith,
      };
    },

    bedsInputProps() {
      return {
        [AutocompleteProperty.Name]: MarketDataFilterFormControlName.Beds,
        [AutocompleteProperty.Options]: OptionsHelper.mapTextAndValueIntoOptions(
          DEFAULT_MULTI_BEDS_OPTIONS,
        ),
        [AutocompleteProperty.Label]: 'Beds',
        [SelectProperty.Multiple]: true,
        [AutocompleteProperty.FillType]: InputFillType.Underlined,
        [AutocompleteProperty.FilterStrategy]: AutocompleteFilterStrategy.Sync,
        [AutocompleteProperty.FilterType]: AutocompleteFilterType.StartsWith,
      };
    },

    bathsInputProps() {
      return {
        [AutocompleteProperty.Name]: MarketDataFilterFormControlName.Baths,
        [AutocompleteProperty.Options]: OptionsHelper.mapTextAndValueIntoOptions(
          DEFAULT_MULTI_BATHS_OPTIONS,
        ),
        [AutocompleteProperty.Label]: 'Baths',
        [SelectProperty.Multiple]: true,
        [AutocompleteProperty.FillType]: InputFillType.Underlined,
        [AutocompleteProperty.FilterStrategy]: AutocompleteFilterStrategy.Sync,
        [AutocompleteProperty.FilterType]: AutocompleteFilterType.StartsWith,
      };
    },

    yearBuiltSelectValue() {
      return {
        min: this.formGroup.value[MarketDataFilterFormControlName.MinYearBuilt],
        max: this.formGroup.value[MarketDataFilterFormControlName.MaxYearBuilt],
      };
    },

    priceSelectValue() {
      return {
        min: this.formGroup.value[MarketDataFilterFormControlName.MinPrice],
        max: this.formGroup.value[MarketDataFilterFormControlName.MaxPrice],
      };
    },
  },
  watch: {
    province(value) {
      this.loadAddressDictionaries(value);
    },
  },
  mounted() {
    this.loadAddressDictionaries(this.province);
  },
  methods: {
    async loadAddressDictionaries(province) {
      if (province) {
        await this.getAddressDictionaries(province);
      }
    },
    async handleSubmit() {
      const isAuthenticated = await this.checkAuthAndRedirect();

      this.formGroup.validate();

      if (this.formGroup.isValid && isAuthenticated) {
        this.trackSubmit({
          userId: this.user.userId,
          email: this.user.email,
        });
        this.$emit(FormEventType.Submit, this.formGroup.value);
      }
    },
    handleReset() {
      this.trackReset();
      this.formGroup.reset();
    },
    handleProvinceChange() {
      this.cityControl
        .setValue(MARKET_DATA_FILTER_FORM_DEFAULT_VALUE[MarketDataFilterFormControlName.City]);
      this.communityControl
        .setValue(MARKET_DATA_FILTER_FORM_DEFAULT_VALUE[MarketDataFilterFormControlName.Community]);
    },
    onYearBuiltChange({ min, max, action }) {
      if (action === MinMaxSelectId.Min) {
        this.formGroup.getControl(MarketDataFilterFormControlName.MinYearBuilt).setValue(min);
        this.handleFormChange();
        this.trackChange(this.formGroup.value);
      }

      if (action === MinMaxSelectId.Max) {
        this.formGroup.getControl(MarketDataFilterFormControlName.MaxYearBuilt).setValue(max);
        this.handleFormChange();
        this.trackChange(this.formGroup.value);
      }
    },
    onPriceChange({ min, max, action }) {
      if (action === MinMaxSelectId.Min) {
        this.formGroup.getControl(MarketDataFilterFormControlName.MinPrice).setValue(min);
        this.handleFormChange();
        this.trackChange(this.formGroup.value);
      }

      if (action === MinMaxSelectId.Max) {
        this.formGroup.getControl(MarketDataFilterFormControlName.MaxPrice).setValue(max);
        this.handleFormChange();
        this.trackChange(this.formGroup.value);
      }
    },
  },
};
